import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { type Section } from "~/api/types";
import { Badge } from "~/atoms/badge";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from "~/atoms/item";
import { cn } from "~/lib/utils";
import { getDraftDocumentsNotUsingLatestSection } from "~/helpers/sections";

export const OutdatedVersionsBadge = () => {
  const { t } = useTranslation();

  return (
    <Badge variant="warn" className="flex gap-1">
      <AlertTriangle className="size-3" />
      {t(($) => $.commons.sections.outdatedVersionsWarning)}
    </Badge>
  );
};

interface SectionsContainerProps {
  sections: Section[];
  title: string;
  selectedSectionId?: number;
  onSectionClick: (section: Section) => void;
  usedSection: boolean;
}

const SectionsContainer = ({
  sections,
  title,
  selectedSectionId,
  onSectionClick,
  usedSection,
}: SectionsContainerProps) => {
  const { t } = useTranslation();

  const hasOutdatedVersionsInUse = (section: Section) => {
    return getDraftDocumentsNotUsingLatestSection(section) !== null;
  };

  const calculateNumberOfUsages = (section: Section) => {
    let usages = 0;
    section.sectionVersions.forEach((sv) => {
      usages = usages + sv.usedInDocumentVersions.length;
    });

    return usages;
  };

  return (
    <div className="flex min-h-0 flex-auto flex-col gap-2">
      <p className="text-sm font-medium">{title}</p>
      <ItemGroup className="gap-0">
        {sections.map((section) => (
          <Item
            key={section.id}
            variant="outline"
            className={cn(
              "cursor-pointer rounded-none border-b-0 first:rounded-t-xl last:rounded-b-xl last:border-b-1",
              selectedSectionId && section.id === selectedSectionId
                ? "bg-muted"
                : "hover:bg-muted/50",
            )}
            onClick={() => onSectionClick(section)}
          >
            <ItemContent>
              <ItemTitle>
                {section.name}
                {usedSection && hasOutdatedVersionsInUse(section) && (
                  <OutdatedVersionsBadge />
                )}
              </ItemTitle>
              <ItemDescription className="flex flex-col">
                <span>
                  {t(($) => $.commons.sections.versionsCount, {
                    count: section.sectionVersions.length,
                  })}
                </span>
                {usedSection ? (
                  <span>
                    {t(($) => $.commons.sections.usedInDocumentsCount, {
                      count: calculateNumberOfUsages(section),
                    })}
                  </span>
                ) : (
                  <span className="italic">
                    {t(($) => $.commons.sections.noDocuments)}
                  </span>
                )}
              </ItemDescription>
            </ItemContent>
          </Item>
        ))}
      </ItemGroup>
    </div>
  );
};

interface SectionsPanelProps {
  sections: Section[];
  onSectionClick: (section: Section) => void;
  selectedSection: Section | null;
}

export const SectionsPanel = ({
  sections,
  onSectionClick,
  selectedSection,
}: SectionsPanelProps) => {
  const { t } = useTranslation();
  const sectionHasUsage = (section: Section) => {
    return section.sectionVersions.some(
      (sv) => sv.usedInDocumentVersions.length > 0,
    );
  };

  sections.sort((a, b) => {
    return a.name.localeCompare(b.name);
  });

  const usedSections: Section[] = [];
  const unUsedSections: Section[] = [];

  sections.forEach((s) => {
    if (sectionHasUsage(s)) {
      usedSections.push(s);
    } else {
      unUsedSections.push(s);
    }
  });

  return (
    <div className="flex h-full flex-col gap-10">
      {usedSections.length > 0 && (
        <SectionsContainer
          sections={usedSections}
          title={t(($) => $.commons.sections.inDocuments)}
          selectedSectionId={selectedSection?.id}
          onSectionClick={onSectionClick}
          usedSection={true}
        />
      )}

      {unUsedSections.length > 0 && (
        <SectionsContainer
          sections={unUsedSections}
          title={t(($) => $.commons.sections.unused)}
          selectedSectionId={selectedSection?.id}
          onSectionClick={onSectionClick}
          usedSection={false}
        />
      )}
    </div>
  );
};
